/**
 * Sign a new insurability pack version from the pack page (V111-5-5, extended for v11.6 V116-4-4 / ADR-0114 §9).
 *
 *  - D-113-29 carried exceptions: the preview says what signing would carry (`carried`), so the groups to tick and
 *    the reason show when the dialog opens. A 400 `exceptions_unacknowledged` still re-asks (server backstop).
 *  - Privilege inclusion (design round): only when the draft withholds privileged determinations. The owner picks
 *    which to include, names the readers, picks the signed reason from the list, and acknowledges that this may
 *    waive privilege and that a signed version can't be recalled. It applies to THIS version only. When the
 *    determinations can't be listed, "Include" is disabled and says why (never a silent failure).
 *  - Validation: every error renders next to its field (aria-invalid + aria-describedby), one summary line is
 *    shown, and focus moves to the first invalid field.
 *
 * Which determinations are withheld is read from the AI Regulatory grid (the pack preview carries only the count):
 * privileged, not shared externally, decided or recorded, on a rule of a law in the pack.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Checkbox, Field, Input, Select, Textarea } from '@aigovops/design-system';
import { PRIVILEGE_INCLUSION_REASONS } from '@aigovops/core';
import { ApiError, signInsurabilityPack, type InsurabilityPackSummary, type PrivilegeInclusionReason, type SignInsurabilityPackBody } from '../../lib/api';
import { getAiGrid } from '../../lib/ai-program-api';
import { plural } from '../../lib/format';
import { PRIVILEGE_REASON_LABEL } from '../../lib/pack-legal';
import { ActionDialog, errorText } from '../today/shared';
import { RECALL_TEXT } from './PackSharing';

type Group = 'failing' | 'overdue';
type Counts = { failing: number; overdue: number };

export interface WithheldCandidate { determination_id: string; label: string }

type Errs = { ticks?: string; reason?: string; picked?: string; recipients?: string; reasonCode?: string; ack?: string };

export function PackSignDialog({
  open, slug, nextVersion, gaps, withheld, lawRuleIds, systemNames, defaultRecipients, startWithPrivilege, initialCarried, backPath, onClose, onSigned, cancelLabel,
}: {
  open: boolean;
  slug: string;
  nextVersion: number;
  gaps: number;
  /** legal.withheld_privileged of the draft. */
  withheld: number;
  lawRuleIds: Set<string>;
  systemNames: Map<string, string>;
  /** Named readers (active links and API grants), prefilled into the readers. */
  defaultRecipients: string[];
  startWithPrivilege: boolean;
  /** GET /preview `carried`: what signing this draft would carry (owner / admin). */
  initialCarried: Counts | null;
  /** This page's path, so AI Regulatory scope can link back. */
  backPath: string;
  onClose: () => void;
  onSigned: (p: InsurabilityPackSummary) => void;
  /** Started from the share dialog: Cancel goes back to it. */
  cancelLabel?: string;
}) {
  const uid = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [carried, setCarried] = useState<Counts | null>(null);
  const [ticks, setTicks] = useState<Record<Group, boolean>>({ failing: false, overdue: false });
  const [reason, setReason] = useState('');
  const [include, setInclude] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [recipients, setRecipients] = useState('');
  const [reasonCode, setReasonCode] = useState<PrivilegeInclusionReason | ''>('');
  const [ack, setAck] = useState(false);
  const [errs, setErrs] = useState<Errs>({});

  const grid = useQuery({ queryKey: ['ai-grid', slug], queryFn: () => getAiGrid(slug), enabled: open && withheld > 0, retry: false });
  const candidates: WithheldCandidate[] = useMemo(() => {
    const out: WithheldCandidate[] = [];
    for (const cell of grid.data?.cells ?? []) {
      const d = cell.determination;
      if (!d || !d.privileged || d.visibility === 'external') continue;
      if (d.state !== 'decided' && d.state !== 'recorded') continue;
      if (!d.rule_ids.some((r) => lawRuleIds.has(r))) continue;
      out.push({ determination_id: d.determination_id, label: `${d.law_label} · ${systemNames.get(d.system_id) ?? d.system_id}` });
    }
    return out;
  }, [grid.data, lawRuleIds, systemNames]);
  const canInclude = withheld > 0 && !grid.isLoading && !grid.error && candidates.length > 0;

  useEffect(() => {
    if (!open) return;
    setError(null); setTicks({ failing: false, overdue: false }); setReason('');
    setCarried(initialCarried && (initialCarried.failing > 0 || initialCarried.overdue > 0) ? initialCarried : null);
    setInclude(false); setPicked([]); setRecipients(defaultRecipients.join(', '));
    setReasonCode(''); setAck(false); setErrs({});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  // "Include when signing" opens with the step ticked, once the determinations can be listed.
  useEffect(() => { if (open && startWithPrivilege && canInclude) setInclude(true); }, [open, startWithPrivilege, canInclude]);

  const recipientList = recipients.split(',').map((s) => s.trim()).filter(Boolean);
  const readers = recipientList.length ? recipientList.join(', ') : 'Your readers';
  const groups = (['failing', 'overdue'] as Group[]).filter((g) => (carried?.[g] ?? 0) > 0);
  const ids = {
    ticks: `${uid}-ticks`, reason: `${uid}-reason`, picked: `${uid}-picked`, recipients: `${uid}-recipients`,
    reasonCode: `${uid}-reason-code`, ack: `${uid}-ack`, why: `${uid}-why`,
  };
  const errId = (k: keyof Errs) => `${ids[k]}-err`;
  const errLine = (k: keyof Errs) => (errs[k] ? <p id={errId(k)} className="ip-err">{errs[k]}</p> : null);

  async function submit(): Promise<boolean> {
    const e: Errs = {};
    if (groups.length) {
      if (groups.some((g) => !ticks[g])) e.ticks = 'Tick each carried group.';
      if (!reason.trim()) e.reason = 'Say why the pack is signed with open problems.';
    }
    if (include) {
      if (!picked.length) e.picked = 'Pick at least one determination.';
      if (!recipientList.length) e.recipients = 'Name who will read it.';
      if (!reasonCode) e.reasonCode = 'Pick a reason.';
      if (!ack) e.ack = 'Tick the box to confirm.';
    }
    setErrs(e);
    const order: Array<keyof Errs> = ['ticks', 'reason', 'picked', 'recipients', 'reasonCode', 'ack'];
    const first = order.find((k) => e[k]);
    if (first) {
      const n = order.filter((k) => e[k]).length;
      setError(`Fix ${plural(n, 'item')} below to sign.`);
      const target = first === 'ticks' ? `${uid}-${groups.find((g) => !ticks[g])}`
        : first === 'picked' ? `${uid}-d-${candidates[0]?.determination_id}` : ids[first];
      setTimeout(() => document.getElementById(target)?.focus(), 0);
      return false;
    }
    const body: SignInsurabilityPackBody = {};
    if (groups.length) { body.reason = reason.trim(); body.acknowledge = groups; }
    if (include && reasonCode) body.include_privileged = { determination_ids: picked, reason_code: reasonCode, recipients: recipientList, acknowledged: true };
    setBusy(true);
    setError(null);
    try {
      const { package: p } = await signInsurabilityPack(slug, body);
      onSigned(p);
      return true;
    } catch (err) {
      const b = err instanceof ApiError && err.body && typeof err.body === 'object' ? err.body as { code?: string; carried?: Counts } : null;
      if (err instanceof ApiError && err.status === 400 && b?.code === 'exceptions_unacknowledged' && b.carried) {
        // Server backstop: what it would carry differs from the preview; ask again for those groups.
        setCarried(b.carried);
        setTicks({ failing: false, overdue: false });
        setError('The pack would carry open problems. Add a reason and tick each group.');
      } else if (err instanceof ApiError && err.status === 403) setError('Only an owner or admin can sign the pack.');
      else if (err instanceof ApiError && err.status === 503) setError('Signing isn’t set up for this workspace yet.');
      else setError(errorText(err, 'Could not sign the pack.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  const scopeHref = `/w/${encodeURIComponent(slug)}/app/programs/ai-regulatory/scope?from=${encodeURIComponent(backPath)}&from_label=${encodeURIComponent('Insurability pack')}`;

  return (
    <ActionDialog open={open} title={`Sign version ${nextVersion}?`} submitLabel="Sign" cancelLabel={cancelLabel} onClose={onClose} onSubmit={submit} busy={busy} error={error}
      subtitle="A signed version can’t be edited. Every link opens it once it is signed.">
      {gaps > 0 ? (
        <p className="ip-body"><span aria-hidden="true">⚠ </span>It will list {plural(gaps, 'uncovered exposure')} as open.</p>
      ) : (
        <p className="ip-body"><span aria-hidden="true">✓ </span>Every applicable risk has a mitigating control.</p>
      )}

      {groups.length ? (
        <fieldset className="ip-fieldset" id={ids.ticks} tabIndex={-1} aria-invalid={errs.ticks ? true : undefined} aria-describedby={errs.ticks ? errId('ticks') : undefined} data-testid="ip-carried">
          <legend className="ip-legend">Carried as exceptions · tick each</legend>
          {groups.map((g) => (
            <Checkbox key={g} id={`${uid}-${g}`} checked={ticks[g]}
              label={`I accept ${g === 'failing' ? plural(carried![g], 'failing control') : plural(carried![g], 'overdue check')}`}
              onChange={(ev) => { const c = ev.currentTarget.checked; setTicks((t) => ({ ...t, [g]: c })); setErrs((x) => ({ ...x, ticks: undefined })); }} />
          ))}
          {errLine('ticks')}
          <Field id={ids.reason} label="Reason" required error={errs.reason} errorRole="none">
            <Textarea rows={2} value={reason} onChange={(ev) => { setReason(ev.currentTarget.value); setErrs((x) => ({ ...x, reason: undefined })); }} />
          </Field>
        </fieldset>
      ) : null}

      {withheld > 0 ? (
        <div className="ip-stack ip-divided">
          <Checkbox id={`${uid}-include`} checked={include} disabled={!canInclude}
            aria-describedby={!canInclude ? ids.why : undefined}
            label={`Include privileged determinations in version ${nextVersion} (${withheld} withheld)`}
            onChange={(ev) => setInclude(ev.currentTarget.checked)} />
          {!canInclude ? (
            <p id={ids.why} className="ip-muted ip-flat" data-testid="ip-priv-why">
              {grid.isLoading ? 'Checking which determinations are withheld…'
                : <>Can’t include them here: {grid.error ? 'the AI Regulatory determinations couldn’t be read' : 'they can’t be listed for this pack'}.{' '}
                  {grid.error ? <><Button variant="ghost" compact onClick={() => void grid.refetch()}>Retry</Button>{' '}</> : null}
                  <Link to={scopeHref}>Open AI Regulatory scope</Link></>}
            </p>
          ) : null}
          {include && canInclude ? (
            <div className="ip-stack" data-testid="ip-priv-step">
              <fieldset className="ip-fieldset" id={ids.picked} aria-invalid={errs.picked ? true : undefined} aria-describedby={errs.picked ? errId('picked') : undefined}>
                <legend className="ip-legend">Determinations to include</legend>
                {candidates.map((c) => (
                  <Checkbox key={c.determination_id} id={`${uid}-d-${c.determination_id}`} label={c.label} checked={picked.includes(c.determination_id)}
                    onChange={(ev) => { const on = ev.currentTarget.checked; setPicked((p) => on ? [...p, c.determination_id] : p.filter((x) => x !== c.determination_id)); setErrs((x) => ({ ...x, picked: undefined })); }} />
                ))}
                {errLine('picked')}
              </fieldset>
              <Field id={ids.recipients} label="Readers" required error={errs.recipients} errorRole="none" hint="Separate names with commas">
                <Input value={recipients} onChange={(ev) => { setRecipients(ev.currentTarget.value); setErrs((x) => ({ ...x, recipients: undefined })); }} />
              </Field>
              <Field id={ids.reasonCode} label="Signed reason" required error={errs.reasonCode} errorRole="none">
                <Select value={reasonCode} onChange={(ev) => { setReasonCode(ev.currentTarget.value as PrivilegeInclusionReason | ''); setErrs((x) => ({ ...x, reasonCode: undefined })); }}>
                  <option value="">Pick a reason</option>
                  {PRIVILEGE_INCLUSION_REASONS.map((r) => <option key={r} value={r}>{PRIVILEGE_REASON_LABEL[r]}</option>)}
                </Select>
              </Field>
              <p className="ip-muted ip-flat">Goes into version {nextVersion} only.</p>
              <Checkbox id={ids.ack} checked={ack}
                aria-invalid={errs.ack ? true : undefined} aria-describedby={errs.ack ? errId('ack') : undefined}
                label={`May waive privilege. ${readers}, and anyone they forward the link to, can read it. ${RECALL_TEXT}`}
                onChange={(ev) => { setAck(ev.currentTarget.checked); setErrs((x) => ({ ...x, ack: undefined })); }} />
              {errLine('ack')}
            </div>
          ) : null}
        </div>
      ) : null}
    </ActionDialog>
  );
}
